import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { IChoosenTeamsInterface } from 'app/library/all-users/interfaces/choosen-teams.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { AudienceResource } from 'app/library/grow/models/audience-resource.model';
import { UNASSIGNED_TEAM_FILTER_ID, UNASSIGNED_TEAM_FILTER_TYPE } from 'app/library/team/constants/unassigned-team-filter.constant';
import { ITeamRoleFilterOption } from 'app/library/team/interfaces/team-role-filter-option.interface';
import { Team } from 'app/library/team/models/team.model';
import { TeamRoleFilterOptions } from 'app/library/team/models/team-role-filter-options.model';
import { TeamApiService } from 'app/library/team/services/team.api-service';
import { TeamService } from 'app/library/team/services/team.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { IEntityNode } from 'app/projects/shared/src/lib/interfaces/entity-node.interface';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-team-filter',
    templateUrl: './team-filter.component.html',
    styleUrls: ['./team-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TeamFilterComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    preSelectUnassigned: boolean;

    @ViewChild('teamFilterScrollArea', { read: ElementRef })
    private _teamFilterScrollArea?: ElementRef<HTMLElement>;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    @Output()
    choosenTeams = new EventEmitter<IChoosenTeamsInterface>();

    treeControl = new NestedTreeControl<IEntityNode>((node) => node.children);
    dataSource = new MatTreeNestedDataSource<IEntityNode>();
    displayedColumns = ['name', 'buttons'];

    list: IChoosenFilterInterface[] = [];
    isTableActive = true;
    searchInput = new FormControl();
    isLoading = true;
    isUnassignedSelected: boolean;
    teams: Team[] = [];
    expandedNodes: string[] = [];
    originalData: IEntityNode[] = [];

    isSearchActive = false;
    isSearchLoading = false;
    searchResults: Team[] = [];
    searchTotalCount = 0;
    searchPageSize = 10;
    searchPageIndex = 0;
    excludedTeamIds: string[] = [];
    teamRoleOptions: ITeamRoleFilterOption[] = TeamRoleFilterOptions.createDefaultOptions();
    private _hadUnassignedChip = false;

    constructor(
        private _teamService: TeamService,
        private _teamApiService: TeamApiService,
        private _allUsersService: AllUsersApiService,
        private _translateService: TranslateService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        await this._teamService.fetchRootTeams();

        this.isUnassignedSelected = this.preSelectUnassigned;
        this.isTableActive = !this.isUnassignedSelected;

        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this._syncExcludedTeamIdsFromList();
            this._syncUnassignedSelectionFromList();
            this._restoreTeamRolesFromList();
            this.updateSelectionState();
        });

        if (this.isUnassignedSelected && !this._hasUnassignedTeamFilter()) {
            this._applyUnassignedTeamFilter(true);
        }

        this.searchInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe((searchTerm) => {
            const trimmedSearchTerm = (searchTerm || '').trim();

            if (trimmedSearchTerm) {
                this.isSearchActive = true;
                this.searchPageIndex = 0;
                this.loadSearchResults();
            } else {
                this.isSearchActive = false;
                this.searchResults = [];
                this.searchTotalCount = 0;
            }
        });

        this._teamService.rootTeams$
            .pipe(
                filter((teams) => !!teams),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((teams) => {
                this.teams = teams;
                this.isLoading = false;
                this.buildTreeFromRootTeams();
            });
    }

    hasChild = (_: number, node: IEntityNode) => (!!node.children && node.children.length > 0) || node.childrenCount > 0;

    async toggleNode(node: IEntityNode): Promise<void> {
        if (this.treeControl.isExpanded(node)) {
            this.treeControl.collapse(node);
            return;
        }

        if (node.childrenCount > 0 && (!node.children || node.children.length === 0)) {
            node.isLoading = true;
            this.treeControl.expand(node);

            const children = await this._teamService.fetchParentChildren(node.id);
            if (children) {
                node.children = children.map((child) => ({
                    id: child.id,
                    name: child.name,
                    children: [],
                    childrenCount: child.subteamCount || 0,
                    parent: { id: node.id },
                    isSelected: false,
                }));
                node.children.forEach((childNode) => {
                    childNode.isSelected = this._isTeamNodeSelected(childNode);
                });
            }

            node.isLoading = false;
            this.refreshDataSource();
            this.treeControl.expand(node);
            this.originalData = this.collectAllNodes();
            this._scheduleScrollSelectedTeamIntoView();
        } else {
            this.treeControl.expand(node);
        }
    }

    buildTreeFromRootTeams(): void {
        const listTree: IEntityNode[] = this.teams.map((team) => ({
            id: team.id,
            name: team.name,
            children: [],
            childrenCount: team.subteamCount || 0,
            parent: team.parent,
            isSelected: this._isExplicitTeamSelected(team.id),
        }));

        this.dataSource.data = listTree;
        this.originalData = listTree;
        this._scheduleScrollSelectedTeamIntoView();
    }

    updateSelectionState(): void {
        const updateNodes = (nodes: IEntityNode[]): void => {
            nodes.forEach((node) => {
                node.isSelected = this._isTeamNodeSelected(node);
                if (node.children && node.children.length > 0) {
                    updateNodes(node.children);
                }
            });
        };

        if (this.originalData.length) {
            updateNodes(this.originalData);
        }

        // Always sync the rendered tree: after expand, `originalData` is rebuilt via
        // `collectAllNodes()` and no longer shares references with `dataSource.data`.
        if (this.dataSource.data?.length) {
            updateNodes(this.dataSource.data);
        }

        if (this.originalData.length || this.dataSource.data?.length) {
            this.refreshDataSource();
            this._scheduleScrollSelectedTeamIntoView();
        }

        this._updateSearchResultsSelection();
    }

    loadSearchResults(): void {
        this.isSearchLoading = true;
        this._teamApiService
            .searchTeamsTree((this.searchInput.value || '').trim(), this.searchPageIndex + 1, this.searchPageSize)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                this.searchResults = response.data.reduce<Team[]>((acc, tree) => {
                    const teams = tree.teams.map((t) => new Team(t));
                    return [...acc, ...teams];
                }, []);
                this.searchTotalCount = response.pagination.number_of_results;
                this._updateSearchResultsSelection();
                this.isSearchLoading = false;
            });
    }

    onSearchPageChange(event: PageEvent): void {
        this.searchPageIndex = event.pageIndex;
        this.loadSearchResults();
    }

    private _updateSearchResultsSelection(): void {
        this.searchResults.forEach((team) => {
            team.isSelected = this._isTeamResourceSelected(team);
        });
    }

    private _scheduleScrollSelectedTeamIntoView(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => this._scrollSelectedTeamRowIntoView());
        });
    }

    private _scrollSelectedTeamRowIntoView(): void {
        const host = this._teamFilterScrollArea?.nativeElement;
        if (!host) {
            return;
        }

        const selectedEl = host.querySelector<HTMLElement>('[data-team-selected-row]');
        selectedEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    addToList(team: AudienceResource): void {
        if (this.isUnassignedSelected || this._hasUnassignedTeamFilter()) {
            this.isUnassignedSelected = false;
            this.list = this.list.filter((item) => item.entity.type !== UNASSIGNED_TEAM_FILTER_TYPE);
        }

        if (this._hasSelectedAncestor(team)) {
            this._includeTeamInSelectedParent(team);
            return;
        }

        const maxBrainTeam = this.convertToMaxBrainTeam(team);
        const selectedTeamAndDescendantIds = [maxBrainTeam.id, ...this._getKnownDescendantIds(team)];
        this._emitUpdatedList([
            ...this.list.filter((item) => item.entity.type !== 'Team' || !selectedTeamAndDescendantIds.includes(item.entity.id)),
            {
                entity: { id: maxBrainTeam.id, type: 'Team', excludedTeamIds: [] },
                valueToShow: this._buildTeamValueToShow(maxBrainTeam.name),
            },
        ]);
    }

    removeFromList(team: AudienceResource): void {
        if (this._isExplicitTeamSelected(team.id)) {
            const selectedTeamAndDescendantIds = [team.id, ...this._getKnownDescendantIds(team)];
            this._emitUpdatedList(this.list.filter((item) => item.entity.type !== 'Team' || !selectedTeamAndDescendantIds.includes(item.entity.id)));
            return;
        }

        if (this._hasSelectedAncestor(team)) {
            this._excludeTeamFromSelectedParent(team);
            return;
        }

        this._emitUpdatedList(this.list.filter((item) => item.entity.id !== team.id));
    }

    onSelection(): void {
        this.isTableActive = !this.isUnassignedSelected;

        if (this.isUnassignedSelected) {
            this._applyUnassignedTeamFilter(true);
            this.choosenTeams.emit({ teams: [], excludedTeamIds: [], isUnassignedSelected: true, teamRoleIds: [] });
            return;
        }

        this._applyUnassignedTeamFilter(false);
        this.choosenTeams.emit({
            teams: this.dataSource.data.filter((team) => this._isExplicitTeamSelected(team.id)),
            excludedTeamIds: this.excludedTeamIds,
            isUnassignedSelected: false,
            teamRoleIds: this._getSelectedTeamRoleIds(),
        });
    }

    onTeamRoleChange(option: ITeamRoleFilterOption, checked: boolean): void {
        if (!checked && this._getSelectedTeamRoleCount() <= 1) {
            option.selected = true;
            return;
        }

        option.selected = checked;
        this._syncTeamRolesToFilterList();
        this.onSelection();
    }

    convertToMaxBrainTeam(teamResource: AudienceResource): Team {
        const maxBrainTeam = new Team();
        maxBrainTeam.id = teamResource.id;
        maxBrainTeam.name = teamResource.name;
        maxBrainTeam.isSelected = teamResource.isSelected;

        return maxBrainTeam;
    }

    private collectAllNodes(): IEntityNode[] {
        const collect = (nodes: IEntityNode[]): IEntityNode[] => {
            return nodes.map((node) => ({
                ...node,
                children: node.children ? collect(node.children) : [],
            }));
        };
        return collect(this.dataSource.data);
    }

    private refreshDataSource(): void {
        const data = this.dataSource.data;
        this.dataSource.data = null;
        this.dataSource.data = data;
    }

    private _emitUpdatedList(updatedList: IChoosenFilterInterface[]): void {
        this.list = updatedList;
        this._syncExcludedTeamIdsFromList();
        this.updateSelectionState();
        this.addedToList.emit(updatedList);
    }

    private _hasUnassignedTeamFilter(): boolean {
        return this.list.some((item) => item.entity.type === UNASSIGNED_TEAM_FILTER_TYPE);
    }

    private _syncUnassignedSelectionFromList(): void {
        const hasUnassignedChip = this._hasUnassignedTeamFilter();

        if (hasUnassignedChip) {
            this._hadUnassignedChip = true;
            this.isTableActive = false;

            if (!this.isUnassignedSelected) {
                this.isUnassignedSelected = true;
                this.choosenTeams.emit({ teams: [], excludedTeamIds: [], isUnassignedSelected: true, teamRoleIds: [] });
            }
            return;
        }

        if (this._hadUnassignedChip && this.isUnassignedSelected) {
            this._hadUnassignedChip = false;
            this.isUnassignedSelected = false;
            this.isTableActive = true;
            this.choosenTeams.emit({
                teams: this.dataSource.data.filter((team) => this._isExplicitTeamSelected(team.id)),
                excludedTeamIds: this.excludedTeamIds,
                isUnassignedSelected: false,
                teamRoleIds: this._getSelectedTeamRoleIds(),
            });
            return;
        }

        this._hadUnassignedChip = false;
        this.isTableActive = !this.isUnassignedSelected;
    }

    private _applyUnassignedTeamFilter(isSelected: boolean): void {
        if (isSelected) {
            this.teamRoleOptions.forEach((option) => {
                option.selected = true;
            });

            const nonTeamFilters = this.list.filter(
                (item) => item.entity.type !== 'Team' && item.entity.type !== 'TeamRole' && item.entity.type !== UNASSIGNED_TEAM_FILTER_TYPE
            );

            this._emitUpdatedList([
                ...nonTeamFilters,
                {
                    entity: { id: UNASSIGNED_TEAM_FILTER_ID, type: UNASSIGNED_TEAM_FILTER_TYPE },
                    valueToShow: `${this._translateService.instant('TEAMS.TITLE.SINGULAR')}: ${this._translateService.instant(
                        'GENERAL.LABEL.UNASSIGNED_TEAM'
                    )}`,
                },
            ]);
            return;
        }

        if (!this._hasUnassignedTeamFilter()) {
            return;
        }

        this._emitUpdatedList(this.list.filter((item) => item.entity.type !== UNASSIGNED_TEAM_FILTER_TYPE));
    }

    private _restoreTeamRolesFromList(): void {
        const teamRoleFilters = this.list.filter((item) => item.entity.type === 'TeamRole');

        if (!teamRoleFilters.length) {
            this.teamRoleOptions.forEach((option) => {
                option.selected = true;
            });
            return;
        }

        this.teamRoleOptions.forEach((option) => {
            option.selected = teamRoleFilters.some((filter) => filter.entity.id === option.id);
        });
    }

    private _syncTeamRolesToFilterList(): void {
        const nonTeamRoleItems = this.list.filter((item) => item.entity.type !== 'TeamRole');
        const allRolesSelected = this.teamRoleOptions.every((option) => option.selected);
        const teamRoleItems: IChoosenFilterInterface[] = allRolesSelected
            ? []
            : this.teamRoleOptions
                  .filter((option) => option.selected)
                  .map((option) => ({
                      entity: { id: option.id, type: 'TeamRole' as const },
                      valueToShow: `${this._translateService.instant('USERS.FILTER.TEAM_ROLE.LABEL')}: ${this._translateService.instant(option.translationKey)}`,
                  }));

        this.list = [...nonTeamRoleItems, ...teamRoleItems];
        this.addedToList.emit(this.list);
    }

    private _getSelectedTeamRoleIds(): string[] {
        return this.teamRoleOptions.filter((option) => option.selected).map((option) => option.id);
    }

    private _getSelectedTeamRoleCount(): number {
        return this.teamRoleOptions.filter((option) => option.selected).length;
    }

    private _syncExcludedTeamIdsFromList(): void {
        const excludedTeamIds = this.list
            .filter((item) => item.entity.type === 'Team')
            .reduce<string[]>((acc, item) => [...acc, ...(item.entity.excludedTeamIds || [])], []);

        this.excludedTeamIds = Array.from(new Set(excludedTeamIds));
    }

    private _buildTeamValueToShow(teamName: string, excludedTeamIds: string[] = []): string {
        const label = excludedTeamIds.length ? this._translateService.instant('TEAMS.LABEL.PARTIAL_SELECTION', { teamName }) : teamName;
        return `${this._translateService.instant('TEAMS.TITLE.SINGULAR')}: ${label}`;
    }

    private _isExplicitTeamSelected(teamId: string): boolean {
        return this.list.some((item) => item.entity.id === teamId && item.entity.type === 'Team');
    }

    private _getTeamFilter(teamId: string): IChoosenFilterInterface | undefined {
        return this.list.find((item) => item.entity.id === teamId && item.entity.type === 'Team');
    }

    private _isTeamNodeSelected(node: IEntityNode): boolean {
        const path = this._getNodePath(node);
        return this._isPathSelected(path.length ? path : [node]);
    }

    private _isTeamResourceSelected(team: Team): boolean {
        return this._isPathSelected(this._getTeamResourcePath(team));
    }

    private _hasSelectedAncestor(team: AudienceResource | Team): boolean {
        return !!this._getSelectedAncestorId(this._getTeamPath(team));
    }

    private _isPathSelected(path: { id: string }[]): boolean {
        const selectedAncestorId = this._getSelectedAncestorId(path);
        if (!selectedAncestorId) {
            return false;
        }

        const selectedIndex = path.findIndex((item) => item.id === selectedAncestorId);
        const descendantPath = path.slice(selectedIndex + 1);
        return !descendantPath.some((item) => this.excludedTeamIds.includes(item.id));
    }

    private _getSelectedAncestorId(path: { id: string }[]): string {
        const selectedAncestor = path.find((item) => this._isExplicitTeamSelected(item.id));
        return selectedAncestor?.id;
    }

    private _excludeTeamFromSelectedParent(team: AudienceResource | Team): void {
        const path = this._getTeamPath(team);
        const selectedAncestorId = this._getSelectedAncestorId(path);
        const selectedTeamFilter = selectedAncestorId ? this._getTeamFilter(selectedAncestorId) : null;

        if (!selectedTeamFilter || selectedTeamFilter.entity.id === team.id) {
            return;
        }

        const descendantIds = this._getKnownDescendantIds(team);
        const nextExcludedTeamIds = Array.from(
            new Set([...(selectedTeamFilter.entity.excludedTeamIds || []).filter((teamId) => !descendantIds.includes(teamId)), team.id])
        );

        this._updateSelectedTeamFilterExclusions(selectedTeamFilter.entity.id, nextExcludedTeamIds);
    }

    private _includeTeamInSelectedParent(team: AudienceResource | Team): void {
        const path = this._getTeamPath(team);
        const selectedAncestorId = this._getSelectedAncestorId(path);
        const selectedTeamFilter = selectedAncestorId ? this._getTeamFilter(selectedAncestorId) : null;

        if (!selectedTeamFilter) {
            return;
        }

        const pathIds = path.map((pathItem) => pathItem.id);
        const descendantIds = this._getKnownDescendantIds(team);
        const excludedTeamIdsToRemove = [...pathIds, ...descendantIds];
        const nextExcludedTeamIds = (selectedTeamFilter.entity.excludedTeamIds || []).filter((teamId) => !excludedTeamIdsToRemove.includes(teamId));

        this._updateSelectedTeamFilterExclusions(selectedTeamFilter.entity.id, nextExcludedTeamIds);
    }

    private _updateSelectedTeamFilterExclusions(selectedTeamId: string, excludedTeamIds: string[]): void {
        this._emitUpdatedList(
            this.list.map((item) => {
                if (item.entity.type !== 'Team' || item.entity.id !== selectedTeamId) {
                    return item;
                }

                return {
                    ...item,
                    entity: {
                        ...item.entity,
                        excludedTeamIds,
                    },
                    valueToShow: this._buildTeamValueToShow(this._getTeamNameFromValueToShow(item.valueToShow), excludedTeamIds),
                };
            })
        );
    }

    private _getTeamNameFromValueToShow(valueToShow: string): string {
        const idx = valueToShow.indexOf(': ');
        const label = idx === -1 ? valueToShow : valueToShow.slice(idx + 2);
        const partialSuffix = this._translateService.instant('TEAMS.LABEL.PARTIAL_SELECTION', { teamName: '' });
        return label.endsWith(partialSuffix) ? label.slice(0, -partialSuffix.length) : label;
    }

    private _getTeamPath(team: AudienceResource | Team): { id: string }[] {
        const node = this._findNodeById(team.id);
        if (node) {
            return this._getNodePath(node);
        }

        return this._getTeamResourcePath(team);
    }

    private _getTeamResourcePath(team: AudienceResource | Team): { id: string }[] {
        const teamWithBreadcrumbs = team as unknown as Team;
        const breadcrumbs = teamWithBreadcrumbs.breadcrumbs?.length ? teamWithBreadcrumbs.breadcrumbs.map((breadcrumb) => ({ id: breadcrumb.id })) : [];

        if (!breadcrumbs.some((breadcrumb) => breadcrumb.id === team.id)) {
            breadcrumbs.push({ id: team.id });
        }

        return breadcrumbs.length ? breadcrumbs : [{ id: team.id }];
    }

    private _getNodePath(node: IEntityNode): IEntityNode[] {
        const path = this._findNodePath(this.dataSource.data, node.id);
        return path || [node];
    }

    private _findNodePath(nodes: IEntityNode[], teamId: string, parents: IEntityNode[] = []): IEntityNode[] | null {
        for (const node of nodes) {
            const path = [...parents, node];
            if (node.id === teamId) {
                return path;
            }

            if (node.children?.length) {
                const childPath = this._findNodePath(node.children, teamId, path);
                if (childPath) {
                    return childPath;
                }
            }
        }

        return null;
    }

    private _findNodeById(teamId: string): IEntityNode | null {
        const find = (nodes: IEntityNode[]): IEntityNode | null => {
            for (const node of nodes) {
                if (node.id === teamId) {
                    return node;
                }

                const child = node.children?.length ? find(node.children) : null;
                if (child) {
                    return child;
                }
            }

            return null;
        };

        return find(this.dataSource.data);
    }

    private _getKnownDescendantIds(team: AudienceResource | Team): string[] {
        const node = this._findNodeById(team.id);
        if (!node?.children?.length) {
            return [];
        }

        const collect = (nodes: IEntityNode[]): string[] => {
            return nodes.reduce<string[]>((acc, child) => [...acc, child.id, ...(child.children?.length ? collect(child.children) : [])], []);
        };

        return collect(node.children);
    }
}
